/**
 * AirflowSignal
 *
 * SHS Visual Experience System — Sprint 29.2A.
 *
 * First member of the SHS physiology visual language. A stylized, abstract,
 * respiratory-flow-shaped waveform designed to make an SHS surface feel
 * "alive" without simulating a medical monitor.
 *
 * NON-DIAGNOSTIC / NON-CLINICAL / NON-USER:
 *   - This waveform does not represent patient data.
 *   - It carries no numeric values, no units, no interpretation.
 *   - It is not linked to any user, session, or assessment.
 *   - It is an ambient brand/education element only.
 *   - `aria-hidden` defaults to true; screen readers will not announce it.
 *
 * Reduced-motion contract:
 *   The component reads useMotionMode(). When `isReduced` is true, the
 *   animation class is not applied, so the underlying `<g>` sits at its
 *   natural resting position (translateX(0)) and the waveform renders as
 *   a static, non-moving trace. No pulsing, no fading, no delayed
 *   entrance. Content is fully visible immediately.
 *
 * SVG technique:
 *   A single deterministic path with period P (= WAVE_PERIOD SVG units).
 *   The path is drawn over 8 * P coordinate units so viewer sees several
 *   waves regardless of viewBox slicing. Because the path is exactly
 *   periodic in P, translating the enclosing <g> by -P produces a
 *   seamless infinite loop with no visible seam — no duplicated element
 *   or JS math is needed to sew the animation together.
 *
 * Animation technique:
 *   Pure CSS keyframe (`@keyframes shs-airflow-drift` in index.css) on
 *   the wrapping <g>. Uses `transform-box: view-box` so the CSS
 *   translation is expressed in SVG user-space units, which keeps the
 *   loop period byte-for-byte matched to WAVE_PERIOD regardless of the
 *   SVG's rendered pixel size.
 *
 * Cost:
 *   - No JS run-loop.
 *   - No React re-render for animation frames.
 *   - The path string is computed once via useMemo and reused.
 *   - `will-change: transform` (in CSS) promotes the group to its own
 *     compositor layer, which yields sub-1 % CPU on modern hardware.
 */

import React, { useMemo } from 'react';
import { useMotionMode } from '../hooks/useMotionMode';

// -- Geometry (SVG user-space units) ----------------------------------------

const WAVE_PERIOD = 300;                  // one breath cycle length (units)
const WAVE_COUNT = 8;                     // 8 * 300 = 2400 units wide
const VIEWBOX_WIDTH = WAVE_PERIOD * WAVE_COUNT;
const VIEWBOX_HEIGHT = 200;
const BASELINE = 100;
const PEAK = 60;                          // inspiration peak (y is inverted)
const TROUGH = 140;                       // expiration trough

/**
 * Build one respiratory-flow-shaped period using four cubic Bézier
 * segments per period:
 *   1. Baseline → inspiration peak    (rise)
 *   2. Peak → baseline                (descend)
 *   3. Baseline → expiration trough   (fall, slightly deeper)
 *   4. Trough → baseline              (recover)
 *
 * The full path is WAVE_COUNT periods concatenated, so the last point
 * lies on the baseline exactly at x = VIEWBOX_WIDTH — which means the
 * pattern is truly periodic and translating by -WAVE_PERIOD produces
 * a seamless loop.
 */
function buildAirflowPath() {
    const q = WAVE_PERIOD / 4;
    let d = `M 0,${BASELINE}`;
    for (let i = 0; i < WAVE_COUNT; i++) {
        const x = i * WAVE_PERIOD;
        d += ` C ${x + q * 0.33},${BASELINE} ${x + q * 0.67},${PEAK} ${x + q},${PEAK}`;
        d += ` C ${x + q * 1.33},${PEAK} ${x + q * 1.67},${BASELINE} ${x + q * 2},${BASELINE}`;
        d += ` C ${x + q * 2.33},${BASELINE} ${x + q * 2.67},${TROUGH} ${x + q * 3},${TROUGH}`;
        d += ` C ${x + q * 3.33},${TROUGH} ${x + q * 3.67},${BASELINE} ${x + q * 4},${BASELINE}`;
    }
    return d;
}

/**
 * AirflowSignal
 *
 * Props:
 *   className          extra classes on the outer <svg>. Callers use this
 *                      to position (absolute), size (height), opacity, and
 *                      color (via `text-*` because the stroke uses
 *                      `currentColor`). Layout, opacity, and color are
 *                      caller concerns — the component intentionally does
 *                      not enforce them so future surfaces can reuse it.
 *   style              inline style overrides for the outer <svg>.
 *   ariaHidden         defaults to true because the waveform is decorative.
 *                      Callers may pass false ONLY if they wrap it in a
 *                      labelled figure, which is not the case in 29.2A.
 *   strokeWidth        override stroke thickness (SVG user units).
 *
 * Reserved for future phases (not implemented in 29.2A):
 *   intensity          reserved API surface for future Clinical Mode
 *                      variants; ignored today.
 */
export const AirflowSignal = ({
    className = '',
    style,
    ariaHidden = true,
    strokeWidth = 2,
    ...rest
}) => {
    const { isReduced } = useMotionMode();
    const d = useMemo(buildAirflowPath, []);
    const groupClass = isReduced ? '' : 'shs-airflow-drift';

    return (
        <svg
            aria-hidden={ariaHidden}
            focusable="false"
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            preserveAspectRatio="xMidYMid slice"
            className={className}
            style={style}
            {...rest}
        >
            <g className={groupClass}>
                <path
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
            </g>
        </svg>
    );
};

export default AirflowSignal;
